import { request } from "@/api/client";

export type CollectionState =
  | "collected"
  | "fresh"
  | "cooldown"
  | "in_progress"
  | "empty"
  | "failed"
  | "missing_identifier"
  | "not_found";

export type PublicArtist = {
  id: number;
  name: string;
  spotify_id: string | null;
  bandsintown_slug: string | null;
  has_collection_identifier: boolean;
  last_collected_at: string | null;
  last_collection_attempt_at: string | null;
  last_collection_status: "success" | "empty" | "failed" | null;
  last_collection_event_count: number | null;
  is_fresh: boolean;
  collection_in_progress: boolean;
};

/** Stored shared evidence for a comparable (database-only aggregate). */
export type ComparableEvidence = { shows: number; venues: number; upcoming: number };

export type ComparableArtist = {
  id: number;
  is_selected: boolean;
  source: string | null;
  resolved_artist_id: number | null;
  created_at: string | null;
  public_artist: PublicArtist;
  evidence?: ComparableEvidence;
};

export type CollectionResponse = {
  public_artist_id: number;
  result: CollectionState;
  fetched: boolean;
  inserted_events: number;
  event_count: number | null;
  last_collected_at: string | null;
  detail: string | null;
};

export type VenueEvidenceEvent = {
  public_artist_id: number;
  artist_name: string;
  event_date: string;
  is_past: boolean;
  ticket_url: string | null;
};

export type VenuePrivateState = {
  artist_venue_id: string | null;
  has_relationship: boolean;
  status: string | null;
  saved: boolean;
  /** Private "hide from my normal results"; independent of the booking pipeline status. */
  dismissed?: boolean;
  dismissed_at?: string | null;
  /** From the existing Pitch record (custom_fields.pitch.contacted_at). */
  contacted_at?: string | null;
};

/** Unresolved venue identity is shown as evidence only, never as a Save/Pitch target. */
export type VenueIdentity = { status: "ok" | "needs_review"; reason: string | null };

export type GraphVenue = {
  global_venue_id: string | null;
  venue_ids: number[];
  name: string;
  city: string | null;
  region: string | null;
  country: string | null;
  capacity: number | null;
  comparable_artist_count: number;
  comparable_artists: { public_artist_id: number; name: string }[];
  event_count: number;
  most_recent_event_date: string | null;
  events: VenueEvidenceEvent[];
  private_state: VenuePrivateState;
  identity: VenueIdentity;
  pitchable: boolean;
};

export type GraphVenuesResponse = {
  artist_id: string;
  comparable_count: number;
  venue_count: number;
  venues: GraphVenue[];
};

const artistHeaders = (artistId?: string | null) => (artistId ? { "X-Artist-Id": artistId } : undefined);

export function fetchComparables(artistId?: string | null) {
  return request<{ artist_id: string; comparables: ComparableArtist[] }>("/discover/comparables", {
    headers: artistHeaders(artistId),
  });
}

export type ComparableCandidate = {
  kind: "public" | "resolved";
  id: number;
  name: string;
  genre: string | null;
  has_venue_history: boolean;
  already_selected: boolean;
};

/** Database-only picker source: shared artists (venue history first) plus the user's own found artists. */
export function fetchComparableCandidates(artistId?: string | null, query?: string) {
  const suffix = query && query.trim() ? `?q=${encodeURIComponent(query.trim())}` : "";
  return request<{ artist_id: string; candidates: ComparableCandidate[] }>(`/discover/comparable-candidates${suffix}`, {
    headers: artistHeaders(artistId),
  });
}

export function addComparable(
  payload: { resolved_artist_id: number } | { public_artist_id: number },
  artistId?: string | null,
) {
  return request<{ created: boolean; comparable: ComparableArtist }>("/discover/comparables", {
    method: "POST",
    body: payload,
    headers: artistHeaders(artistId),
  });
}

export function removeComparable(comparableId: number, artistId?: string | null) {
  return request<{ comparable: ComparableArtist }>(`/discover/comparables/${encodeURIComponent(comparableId)}`, {
    method: "DELETE",
    headers: artistHeaders(artistId),
  });
}

/** Explicit user action only — this is the one Discover call that may reach an external provider. */
export function updateComparableEvidence(comparableId: number, artistId?: string | null) {
  return request<CollectionResponse>(`/discover/comparables/${encodeURIComponent(comparableId)}/collect`, {
    method: "POST",
    headers: artistHeaders(artistId),
  });
}

export function fetchGraphVenues(artistId?: string | null, limit = 500) {
  return request<GraphVenuesResponse>(`/discover/venues?limit=${encodeURIComponent(limit)}`, {
    headers: artistHeaders(artistId),
  });
}

export function saveGraphVenue(globalVenueId: string, artistId?: string | null) {
  return request<{
    created: boolean;
    artist_venue: { id: string; global_venue_id: string; status: string | null; saved: boolean };
  }>(`/discover/venues/${encodeURIComponent(globalVenueId)}/save`, {
    method: "POST",
    headers: artistHeaders(artistId),
  });
}

type DismissalResponse = { dismissed: boolean; dismissed_at: string | null; private_state: VenuePrivateState };

/** Hide a venue from this artist's normal Discover results. Private; the shared venue is untouched. */
export function dismissGraphVenue(globalVenueId: string, artistId?: string | null) {
  return request<DismissalResponse>(`/discover/venues/${encodeURIComponent(globalVenueId)}/dismiss`, {
    method: "POST",
    headers: artistHeaders(artistId),
  });
}

export function restoreGraphVenue(globalVenueId: string, artistId?: string | null) {
  return request<DismissalResponse>(`/discover/venues/${encodeURIComponent(globalVenueId)}/dismiss`, {
    method: "DELETE",
    headers: artistHeaders(artistId),
  });
}

// ---------------------------------------------------------------------------
// Pitch (V3)
// ---------------------------------------------------------------------------

export type PitchQuota = { limit: number; used: number; remaining: number; period_start: string };

export type PitchAiState = { available: boolean; reason: "ai_disabled" | "not_configured" | null };

export type PitchDraft = {
  subject: string;
  body: string;
  recipient: string | null;
  requested_dates: string | null;
  generated: boolean;
  updated_at: string | null;
};

export type PitchState = {
  created?: boolean;
  changed?: boolean;
  venue: {
    global_venue_id: string;
    name: string;
    city: string | null;
    region: string | null;
    country: string | null;
    capacity: number | null;
  };
  state: "prospect" | "contacted";
  contacted_at: string | null;
  contact: { name: string | null; email: string | null };
  draft: PitchDraft | null;
  quota: PitchQuota;
  ai: PitchAiState;
};

const pitchPath = (globalVenueId: string) => `/discover/venues/${encodeURIComponent(globalVenueId)}/pitch`;

/** Opens (or reopens) a pitch. Creates private prospect state only; never contacts or generates. */
export function openPitch(globalVenueId: string, artistId?: string | null) {
  return request<PitchState>(pitchPath(globalVenueId), { method: "POST", headers: artistHeaders(artistId) });
}

/** One metered AI generation (quota-gated). */
export function generatePitch(
  globalVenueId: string,
  payload: { requested_dates?: string | null; notes?: string | null; recipient?: string | null },
  artistId?: string | null,
) {
  return request<{ draft: PitchDraft; quota: PitchQuota }>(`${pitchPath(globalVenueId)}/generate`, {
    method: "POST",
    body: payload,
    headers: artistHeaders(artistId),
  });
}

/** Saves manual edits. No AI, no quota. */
export function savePitchDraft(
  globalVenueId: string,
  payload: { subject: string; body: string; recipient?: string | null },
  artistId?: string | null,
) {
  return request<{ draft: PitchDraft }>(`${pitchPath(globalVenueId)}/draft`, {
    method: "PUT",
    body: payload,
    headers: artistHeaders(artistId),
  });
}

/** Explicit user action after sending elsewhere. */
export function markPitchContacted(globalVenueId: string, artistId?: string | null) {
  return request<PitchState>(`${pitchPath(globalVenueId)}/contacted`, {
    method: "POST",
    headers: artistHeaders(artistId),
  });
}

// ---------------------------------------------------------------------------
// Home summary (V4) — database-only counts
// ---------------------------------------------------------------------------

export type DiscoverSummary = {
  artist_id: string;
  comparable_count: number;
  comparables_with_evidence: number;
  venue_count: number;
  saved_venue_count: number;
  prospect_count: number;
  contacted_count: number;
};

export function fetchDiscoverSummary(artistId?: string | null) {
  return request<DiscoverSummary>("/discover/summary", { headers: artistHeaders(artistId) });
}
